/**
 * Campaign templates — curated starting points for the New Campaign
 * form. Each template captures a real go-to-market story for tripcode:
 * the audience, the angle, and a default CTA that matches the angle.
 *
 * Templates only seed the form. Admins edit anything they want before
 * creating. They're stable identifiers (`id`) so we can later add
 * per-template defaults (e.g., template-specific brand voice, asset
 * starter pack).
 *
 * Brand voice/model/colors deliberately omitted — they're decided once
 * per workspace and live as the form defaults from `_voiceover.ts`.
 * Templates are about narrative, not aesthetics.
 */

import { DEFAULT_MODEL, type ElevenLabsModel } from './types';

// Buckets the New Campaign picker groups templates under. Adding a
// value here is the only change needed to introduce a new group — the
// picker iterates `CATEGORY_ORDER` below.
export type TemplateCategory =
  | 'social-groups'
  | 'adventure'
  | 'events'
  | 'solo'
  | 'premium';

export const CATEGORY_LABEL: Record<TemplateCategory, string> = {
  'social-groups': 'Group getaways',
  adventure: 'Adventure & outdoors',
  events: 'Events & occasions',
  solo: 'Solo & community',
  premium: 'Premium & couples',
};

// Render order for the grouped picker.
export const CATEGORY_ORDER: TemplateCategory[] = [
  'social-groups',
  'solo',
  'adventure',
  'events',
  'premium',
];

export interface CampaignTemplate {
  id: string;
  // Which group this template lives under in the New Campaign picker.
  category: TemplateCategory;
  // One-line label shown on the template card.
  label: string;
  // Two-line elevator pitch shown under the label.
  blurb: string;
  // Form values the template seeds. Admins can override after pick.
  name: string;
  product: string;
  audience: string;
  offer: string;
  proof: string;
  default_cta: string;
  // Optional brand-side overrides — kept open for future per-template
  // tone (e.g., a "premium" template could ship a different voice).
  model?: ElevenLabsModel;
  primary_color?: string;
  // Search queries that align with the template's narrative. The
  // Assets tab surfaces these as clickable Unsplash/Pexels search
  // links so admins can curate a starter library quickly. Auto-seeding
  // these via the Pexels API is the planned next step (see Path B in
  // sub-2.7).
  seed_queries: string[];
  // Optional hint: ids of UGC character profiles that fit this
  // template's angle. Forward-compat for #589 — left unset until a
  // workspace seeds stable, well-known character ids; the New Campaign
  // picker can later surface "works well with <character>" chips.
  recommended_character_ids?: string[];
}

export const CAMPAIGN_TEMPLATES: CampaignTemplate[] = [
  {
    id: 'groupchat-to-trip',
    category: 'social-groups',
    label: 'Group chat → real trip',
    blurb:
      'For the friend group that has talked about a getaway for years but never books one. The chaos-to-itinerary story.',
    name: 'Group chat to real trip',
    product: 'tripcode — turn group chats into trips you actually take',
    audience: 'friends who keep half-planning the same getaway in the group chat',
    offer: 'one shared trip page where dates, vibes, money, and details all live in one place',
    proof:
      'itinerary, invites, expenses, and a chat stream all attached to the same trip — no more "what did we decide?" scrolling',
    default_cta: 'Plan it for real on tripcode',
    seed_queries: [
      'friends laughing on phone',
      'group chat planning trip laptop',
      'friends planning travel',
      'smartphone messaging app',
      'friends excited destination',
      'travel itinerary phone',
    ],
  },
  {
    id: 'mediterranean-sailing',
    category: 'premium',
    label: 'Mediterranean sailing crew',
    blurb:
      'Premium leisure: bareboat charters, eight friends, a week from Split to Hvar. Aspirational, postcard-quality footage.',
    name: 'Mediterranean sailing — summer 2026',
    product: 'tripcode for sailing crews and bareboat charters',
    audience:
      'friend groups planning a week aboard a charter yacht in the Med — Croatia, Greece, Italy',
    offer:
      'one trip page that holds the boat, the crew list, the per-port plan, and the kitty for provisioning + marina fees',
    proof:
      'split the boat cost transparently, vote on harbour stops, share the float plan, attach the skipper licence — all in one place',
    default_cta: 'Plan the charter on tripcode',
    primary_color: '#0E7C9E',
    seed_queries: [
      'sailing yacht mediterranean sunset',
      'bareboat charter croatia',
      'sailboat deck friends',
      'turquoise sea greek island',
      'marina sailing boats',
      'hvar dubrovnik harbour',
    ],
  },
  {
    id: 'bachelorette-weekend',
    category: 'events',
    label: 'Bachelorette / stag weekend',
    blurb:
      'High-energy planning chaos: 12 people, conflicting calendars, secret-surprise budgets. The "make it actually happen" angle.',
    name: 'Bachelorette weekends',
    product: 'tripcode for bachelorette + stag weekends',
    audience:
      'maids of honour and best men running the planning for 8-15 people across time zones and budgets',
    offer:
      'a single planning hub the bride/groom never sees — collect availability, lock the city, split costs, and keep surprises tight',
    proof:
      'private trip pages, anonymous expense splits, invite-only chat, and venue/activity polls without a thread that loses its mind',
    default_cta: 'Run the planning on tripcode',
    primary_color: '#E84368',
    seed_queries: [
      'bachelorette party celebration',
      'group of women cheers cocktails',
      'rooftop bar friends sunset',
      'pool party friends fun',
      'lisbon weekend friends',
      'spa weekend bachelorette',
    ],
  },
  {
    id: 'last-minute-weekend',
    category: 'social-groups',
    label: 'Last-minute long weekend',
    blurb:
      'Urgency play: three weeks out, four friends, somewhere flyable. FOMO-driven, fast-paced, punchy.',
    name: 'Last-minute long weekends',
    product: 'tripcode for spur-of-the-moment getaways',
    audience:
      'small groups deciding three weeks out that they have to escape — Lisbon, Naples, Marrakesh',
    offer:
      'go from "should we just go?" to a booked weekend in under an evening, without spreadsheets or twenty tabs',
    proof:
      'shared dates picker, one-tap polls on destinations, instant expense splits, and a trip page everyone can reference on the plane',
    default_cta: 'Lock the long weekend on tripcode',
    seed_queries: [
      'last minute travel airport',
      'weekend getaway packing',
      'friends spontaneous trip',
      'city break europe',
      'budget travel friends',
      'airport boarding pass smile',
    ],
  },
  {
    id: 'solo-joins-group',
    category: 'solo',
    label: 'Solo traveller joining a crew',
    blurb:
      'Community angle: solo travellers discovering public trips and joining a group that fits their dates + vibe.',
    name: 'Solo travellers joining group trips',
    product: 'tripcode public trips — find a crew, not a tour group',
    audience:
      'solo travellers who want shared adventures without the package-tour atmosphere',
    offer:
      'browse real upcoming trips run by real people, apply to join, fit into a group of 4–10 that matches your dates and pace',
    proof:
      'host-approved invites, reviewed travellers, transparent itineraries and costs before you commit',
    default_cta: 'Find your next trip on tripcode',
    primary_color: '#7E5BEF',
    seed_queries: [
      'solo traveler backpack',
      'meeting strangers travel hostel',
      'group hiking new friends',
      'solo traveler camera city',
      'travelers laughing dinner',
      'solo backpacker mountain',
    ],
  },
  {
    id: 'adventure-crew',
    category: 'adventure',
    label: 'Adventure crew (hiking / skiing)',
    blurb:
      'Outdoor, kinetic, high-stakes logistics. Mountain weeks, gear lists, refuges, who-drives-the-shuttle conversations.',
    name: 'Adventure crew — hiking + ski touring',
    product: 'tripcode for outdoor + alpine crews',
    audience:
      'hikers and ski tourers coordinating multi-day routes — huts booked, gear synced, transport solved',
    offer:
      'one trip page with the route, the hut bookings, the shuttle plan, the gear matrix, and the weather window',
    proof:
      'shared itinerary by day, per-person gear status, refuge confirmations attached, a kitty for the van rental and ski-pass',
    default_cta: 'Send your next route on tripcode',
    primary_color: '#34C28F',
    seed_queries: [
      'group hiking alps mountains',
      'ski touring fresh snow',
      'mountain refuge hut alps',
      'climbers rope summit',
      'gear backpack hiking',
      'tent sunrise mountain',
    ],
  },
  {
    id: 'city-explorer',
    category: 'solo',
    label: 'City explorer weekend',
    blurb:
      'Urban discovery: a curious traveller mapping cafes, galleries, and hidden corners of a new city across a packed weekend.',
    name: 'City explorer weekends',
    product: 'tripcode for city breaks and urban exploring',
    audience:
      'travellers planning a dense weekend in a new city — neighbourhoods, food spots, museums, nightlife',
    offer:
      'a single day-by-day plan with the must-sees pinned, reservations attached, and walking routes that actually flow',
    proof:
      'map-backed itinerary, saved spots by category, opening hours and bookings in one place, share-ready with whoever joins',
    default_cta: 'Map your city weekend on tripcode',
    primary_color: '#F59E0B',
    seed_queries: [
      'person exploring city street',
      'rooftop city skyline travel',
      'cafe culture european city',
      'art gallery museum visitor',
      'city map walking tour',
      'street food market travel',
    ],
  },
  {
    id: 'couple-getaway',
    category: 'premium',
    label: 'Couple getaway',
    blurb:
      'Two people, one well-planned escape: a romantic weekend or anniversary trip where the planning stays effortless.',
    name: 'Couple getaways',
    product: 'tripcode for couples planning a getaway together',
    audience:
      'couples planning a romantic weekend, anniversary trip, or first big trip together',
    offer:
      'one shared plan you build together — the stay, the dinners, the day trips — without one person carrying all the logistics',
    proof:
      'a trip page you both edit, reservations and confirmations attached, a shared budget, surprises kept on a private note',
    default_cta: 'Plan your getaway on tripcode',
    primary_color: '#EC4899',
    seed_queries: [
      'couple travel sunset romantic',
      'couple walking old town',
      'romantic dinner view travel',
      'couple beach getaway',
      'boutique hotel room couple',
      'couple road trip scenic',
    ],
  },
  {
    id: 'digital-nomad-meetup',
    category: 'solo',
    label: 'Digital nomad meetup',
    blurb:
      'Remote workers coordinating a co-living month or workation — wifi, desks, and weekend trips, planned as a group.',
    name: 'Digital nomad workations',
    product: 'tripcode for digital nomads and workation crews',
    audience:
      'remote workers organising a co-living month or workation with people they met online or on the road',
    offer:
      'a shared base plan — the stay, the coworking, the weekend trips — so the logistics are sorted and everyone can just show up and work',
    proof:
      'shared itinerary and accommodation, cost splits for the villa and coworking, weekend trip polls, a chat that keeps the crew in sync',
    default_cta: 'Organise the workation on tripcode',
    primary_color: '#0EA5E9',
    seed_queries: [
      'digital nomad working laptop cafe',
      'coworking space tropical',
      'remote work villa pool',
      'nomads working together beach',
      'laptop ocean view workation',
      'coliving group travel',
    ],
  },
  {
    id: 'surf-beach-camp',
    category: 'adventure',
    label: 'Surf / beach camp',
    blurb:
      'Sun, boards, and a crew: a week at a surf camp where dawn patrols and beach evenings need loose-but-real coordination.',
    name: 'Surf & beach camps',
    product: 'tripcode for surf trips and beach camps',
    audience:
      'friends booking a week at a surf camp or beach house — lessons, board rentals, and the rhythm of the days',
    offer:
      'one trip page for the camp booking, the lesson schedule, the gear, and the kitty — so the week runs without anyone chasing details',
    proof:
      'shared schedule by day, board and wetsuit rentals tracked, transport from the airport sorted, costs split across the crew',
    default_cta: 'Book the surf trip on tripcode',
    primary_color: '#14B8A6',
    seed_queries: [
      'surfers walking beach boards',
      'surf camp tropical sunrise',
      'friends surfing ocean waves',
      'beach house friends summer',
      'longboard sunset silhouette',
      'surfboards lined up beach',
    ],
  },
  {
    id: 'festival-group',
    category: 'events',
    label: 'Festival group',
    blurb:
      'A crew heading to a festival: tickets, tents, meeting points, and the set times everyone wants to catch.',
    name: 'Festival crews',
    product: 'tripcode for festival groups',
    audience:
      'friends going to a music festival together — camping logistics, ticket tiers, and a packed lineup to coordinate',
    offer:
      'one plan for the tickets, the campsite, the travel, and the set times — so the group actually finds each other once the gates open',
    proof:
      'shared lineup picks, campsite and travel details pinned, ticket and cost tracking, a meeting-point plan that survives no signal',
    default_cta: 'Sort the festival trip on tripcode',
    primary_color: '#A855F7',
    seed_queries: [
      'music festival crowd lights',
      'friends festival camping tents',
      'festival stage sunset crowd',
      'group friends festival flags',
      'camping festival morning',
      'concert hands up silhouette',
    ],
  },
];

/**
 * Build a campaign-form seed from a template. Pure function so the
 * form can call it both on pick (snapshot the values into state) and
 * on reset.
 */
export function templateToFormSeed(template: CampaignTemplate): {
  name: string;
  product: string;
  audience: string;
  offer: string;
  proof: string;
  default_cta: string;
  model: ElevenLabsModel;
  primary_color: string;
  background_color: string;
  text_color: string;
  font_family: string;
} {
  return {
    name: template.name,
    product: template.product,
    audience: template.audience,
    offer: template.offer,
    proof: template.proof,
    default_cta: template.default_cta,
    model: template.model ?? DEFAULT_MODEL,
    primary_color: template.primary_color ?? '#E8432D',
    background_color: '#0D0D0D',
    text_color: '#F2F2F7',
    font_family: 'Inter Tight',
  };
}
